<template>
  <NativeDrawer
    v-model="drawerVisible"
    :title="isEdit ? '编辑团队' : '新增团队'"
    size="60%"
    direction="rtl"
    @close="handleClose"
    destroy-on-close
    :before-close="handleDrawerClose"
  >
    <div class="drawer-content">
      <NativeForm
        ref="formRef"
        :model="localFormData"
        :rules="formRules"
        label-width="120px"
        class="team-form"
      >
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>基本信息</span>
          </div>

          <NativeFormItem label="团队名称" prop="name">
            <NativeInput
              v-model="localFormData.name"
              placeholder="请输入团队名称"
              clearable
            />
          </NativeFormItem>

          <NativeFormItem label="团队介绍" prop="description">
            <NativeInput
              v-model="localFormData.description"
              type="textarea"
              :rows="5"
              placeholder="请输入团队介绍，建议100-500字"
              maxlength="500"
              show-word-limit
            />
          </NativeFormItem>
        </div>

        <!-- 优势特点 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>优势特点</span>
          </div>

          <div class="advantages-list">
            <div
              v-for="(item, index) in localFormData.advantages"
              :key="index"
              class="advantage-item"
            >
              <NativeFormItem
                :label="`优势 ${index + 1}`"
                :prop="`advantages.${index}.advantage`"
                :rules="[
                  {
                    required: true,
                    message: '请输入优势内容',
                    trigger: 'blur',
                  },
                ]"
              >
                <NativeInput
                  v-model="item.advantage"
                  placeholder="请输入优势内容"
                  clearable
                >
                  <template #append>
                    <NativeButton
                      type="danger"
                      @click="removeAdvantage(index)"
                      :disabled="localFormData.advantages!.length <= 1"
                    >
                      ×
                    </NativeButton>
                  </template>
                </NativeInput>
              </NativeFormItem>
            </div>
            <NativeButton
              type="primary"
              plain
              @click="addAdvantage"
              style="width: 100%"
            >
              <span class="native-icon" aria-hidden="true">+</span>
              <span>添加优势</span>
            </NativeButton>
          </div>
        </div>

        <!-- 团队照片 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>团队照片</span>
          </div>

          <div class="photographs-list">
            <div
              v-for="(photo, index) in localFormData.photographs"
              :key="index"
              class="photograph-item"
            >
              <NativeFormItem
                :label="`照片 ${index + 1}`"
                :prop="`photographs.${index}.src`"
                :rules="[
                  {
                    message: '请输入照片路径',
                    trigger: 'blur',
                  },
                  {
                    validator: validatePhotoPath,
                    trigger: 'blur',
                  },
                ]"
              >
                <NativeInput
                  v-model="photo.src"
                  placeholder="请输入照片路径,需以jpg|png|JPG|PNG结尾"
                  clearable
                >
                  <template #prepend>
                    <NativeInputNumber
                      v-model="photo.sortOrder"
                      :min="1"
                      :max="999"
                      controls-position="right"
                      style="width: 80px"
                      placeholder="排序"
                    />
                  </template>
                  <template #append>
                    <NativeButton
                      type="danger"
                      @click="removePhotograph(index)"
                      :disabled="localFormData.photographs!.length <= 1"
                    >
                      ×
                    </NativeButton>
                  </template>
                </NativeInput>
              </NativeFormItem>
              <NativeFormItem label="图片描述">
                <NativeInput
                  v-model="photo.alt"
                  placeholder="请输入图片描述（可选）"
                  clearable
                />
              </NativeFormItem>
            </div>
            <NativeButton
              type="primary"
              plain
              @click="addPhotograph"
              style="width: 100%"
            >
              <span class="native-icon" aria-hidden="true">+</span>
              <span>添加照片</span>
            </NativeButton>
          </div>
        </div>
      </NativeForm>
    </div>

    <template #footer>
      <div class="drawer-footer">
        <NativeButton @click="handleCancel" size="default">
          <span class="native-icon" aria-hidden="true">×</span>
          <span>取消</span>
        </NativeButton>
        <NativeButton
          type="primary"
          @click="handleSubmit"
          :loading="submitLoading"
          size="default"
        >
          <span class="native-icon" aria-hidden="true">✓</span>
          <span>确定</span>
        </NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type {
  NativeFormInstance,
  NativeFormRules,
} from "@/components/NativeUI/types";
import type { TeamType } from "@/stores/teams";

// Props
const props = defineProps<{
  visible: boolean;
  isEdit: boolean;
  formData: Partial<TeamType>;
  submitLoading: boolean;
}>();

// Emits
const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "submit", formData: Partial<TeamType>): void;
  (e: "cancel"): void;
}>();

// 表单引用
const formRef = ref<NativeFormInstance>();

// Drawer 显示状态
const drawerVisible = ref(props.visible);

// 原始数据快照(用于对比修改)
const originalData = ref<Partial<TeamType>>({});

// 本地表单数据(避免直接修改 props)
const localFormData = ref<Partial<TeamType>>({
  id: 0,
  name: "",
  description: "",
  advantages: [{ advantage: "", sortOrder: 0 }],
  photographs: [{ src: "", alt: "", sortOrder: 0 }],
});

// 同步表单数据的函数
const syncFormData = () => {
  // 深拷贝原始数据作为快照
  originalData.value = JSON.parse(JSON.stringify(props.formData));

  // 同步到本地表单数据，确保数组字段有默认值
  localFormData.value = {
    ...props.formData,
    advantages:
      props.formData.advantages && props.formData.advantages.length > 0
        ? [...props.formData.advantages]
        : [{ advantage: "", sortOrder: 0 }],
    photographs:
      props.formData.photographs && props.formData.photographs.length > 0
        ? [...props.formData.photographs]
        : [{ src: "", alt: "", sortOrder: 0 }],
  };
};

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    drawerVisible.value = newVal;
    // Drawer 打开时同步数据
    if (newVal) {
      syncFormData();
    }
  },
);

// 监听 isEdit 变化,重新同步数据
watch(
  () => props.isEdit,
  () => {
    if (drawerVisible.value) {
      syncFormData();
    }
  },
);

// 监听 drawerVisible 变化
watch(drawerVisible, (newVal) => {
  emit("update:visible", newVal);
});

// 验证照片路径格式的辅助函数
const isValidPhotoPath = (path: string): boolean => {
  if (!path) return true;
  // 正则表达式结尾可以是jpg|png|JPG|PNG
  return /\.(JPG|jpg|PNG|png)$/.test(path);
};
const isValidFullPhotoPath = (path: string): boolean => {
  if (!path) return true;
  // 正则表达式结尾可以是jpg|png|JPG|PNG
  return /^\/Environment\/.+\.(JPG|jpg|PNG|png)$/.test(path);
};
// 自定义验证器
const validatePhotoPath = (_rule: any, value: string, callback: any) => {
  if (value && !isValidPhotoPath(value)) {
    callback(new Error("照片路径格式不正确，应为：JPG|PNG|jpg|png结尾"));
  } else {
    callback();
  }
};

// 表单验证规则
const formRules: NativeFormRules = {
  name: [
    { required: true, message: "请输入团队名称", trigger: "blur" },
    { min: 2, max: 50, message: "团队名称长度为2-50个字符", trigger: "blur" },
  ],
  description: [{ required: true, message: "请输入团队介绍", trigger: "blur" }],
};

// 添加优势
const addAdvantage = () => {
  localFormData.value.advantages?.push({
    advantage: "",
    sortOrder: localFormData.value.advantages!.length,
  });
};

// 删除优势
const removeAdvantage = (index: number) => {
  if (
    localFormData.value.advantages &&
    localFormData.value.advantages.length > 1
  ) {
    localFormData.value.advantages.splice(index, 1);
    // 重新计算排序
    localFormData.value.advantages.forEach((item, idx) => {
      item.sortOrder = idx;
    });
  }
};

// 添加照片
const addPhotograph = () => {
  localFormData.value.photographs?.push({
    src: "",
    alt: "",
    sortOrder: localFormData.value.photographs!.length,
  });
};

// 删除照片
const removePhotograph = (index: number) => {
  if (localFormData.value.photographs) {
    localFormData.value.photographs.splice(index, 1);
    // 重新计算排序
    localFormData.value.photographs.forEach((item, idx) => {
      item.sortOrder = idx;
    });
  }
};

// 提交
const handleSubmit = async () => {
  if (!formRef.value) return;

  if (
    localFormData.value.advantages?.some((item) => !item.advantage.trim())
  ) {
    window.alert("请填写所有优势内容");
    return;
  }

  const invalidPhoto = localFormData.value.photographs?.find(
    (photo) => photo.src && !isValidPhotoPath(photo.src),
  );
  if (invalidPhoto) {
    window.alert("照片路径格式不正确，应为：JPG|PNG|jpg|png结尾");
    return;
  }

  await formRef.value.validate(async (valid) => {
    if (valid) {
      const data = { ...localFormData.value };

      // 过滤掉空的优势项
      if (data.advantages) {
        data.advantages = data.advantages.filter((item) =>
          item.advantage.trim(),
        );
      }

      // 过滤掉空的照片项并自动补全路径
      if (data.photographs) {
        data.photographs = data.photographs
          .filter((item) => item.src.trim())
          .map((item) => {
            // 自动补全照片路径
            if (item.src && !isValidFullPhotoPath(item.src)) {
              return {
                ...item,
                src: `/Environment/${data.name}/${item.src}`,
              };
            }
            return item;
          });
      }

      // 提交数据
      emit("submit", data);
    }
  });
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

// 取消
const handleCancel = () => {
  // 重置原始数据快照
  originalData.value = {};
  emit("cancel");
  drawerVisible.value = false;
};

// 关闭抽屉
const handleClose = () => {
  // 重置原始数据快照
  originalData.value = {};
  emit("cancel");
};
</script>

<style scoped lang="scss">
.drawer-content {
  height: calc(100% - 60px); // 减去 footer 高度
  overflow-y: auto;
  padding-right: 10px;

  // 自定义滚动条样式
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dcdfe6;
    border-radius: 3px;

    &:hover {
      background-color: #c0c4cc;
    }
  }
}

.team-form {
  padding: 0 10px;

  .form-section {
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 2px solid #dee2e6;
      font-size: 15px;
      font-weight: 600;
      color: #343a40;

      .native-icon {
        font-size: 18px;
        color: #409eff;
      }
    }

    .advantages-list,
    .photographs-list {
      .advantage-item,
      .photograph-item {
        margin-bottom: 16px;
        padding: 12px;
        background-color: #fff;
        border-radius: 4px;
        border: 1px solid #e4e7ed;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 0;
}

// 优化表单项间距
.native-form-item {
  margin-bottom: 18px;
}
</style>
